import { useCallback, useEffect } from 'react';

import { useApiGet, useApiPost, useApiPatch } from '../../common/api/Api.hooks';
import { useIdSearchableFetch } from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import {
  Supplier,
  SupplierPurchaseOrder,
  SupplierPurchaseOrderCreateDto,
  SupplierVersion
} from './Supplier.interface';

export function useCreateSupplier() {
  const { data, isLoading, error, post } = useApiPost<Supplier>('suppliers');
  return {
    createdSupplier: data,
    isCreating: isLoading,
    createError: error,
    createSupplier: post
  };
}

export function useUpdateSupplier(id: number, token?: string) {
  const guest: string = token ? `/guest?token=${token}` : '';
  const url: string = `suppliers/${id}${guest}`;
  const { data, isLoading, error, patch } = useApiPatch<Supplier>(url);
  return {
    updatedSupplier: data,
    isUpdating: isLoading,
    updateError: error,
    updateSupplier: patch
  };
}

export function useCompleteSupplierSurvey(id: number, token?: string) {
  const tokenParam: string = token ? `?token=${token}` : '';
  const url: string = `suppliers/${id}/complete${tokenParam}`;
  const { data, isLoading, error, patch } = useApiPatch<Supplier>(url);
  return {
    completedSupplier: data,
    isCompleting: isLoading,
    completeError: error,
    completeSupplier: patch
  };
}

export function useFindSuppliers() {
  const { data, isLoading, error, get } = useApiGet<PaginatedList<Supplier>>(
    'suppliers'
  );
  const findSuppliers = useIdSearchableFetch(get, WorkflowEntity.Supplier);
  return {
    supplierList: data,
    isLoading,
    loadingError: error,
    findSuppliers
  };
}

export function useFindSupplier(id: number) {
  const { data, isLoading, error, get } = useApiGet<Supplier>(
    `suppliers/${id}`
  );
  return { supplier: data, isLoading, loadingError: error, findSuplier: get };
}

export function useFindSupplierByTask(
  taskId: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<Supplier>(
    'suppliers/task/' + taskId
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { supplier: data, isLoading, loadingError: error, findSuplier: get };
}

export function useFindSupplierVersion(supplierId: number, version: number) {
  const { data, isLoading, error, get } = useApiGet<SupplierVersion>(
    `suppliers/${supplierId}/version/${version}`
  );
  return {
    supplierVersion: data,
    isLoading,
    loadingError: error,
    findSupplierVersion: get
  };
}

export function useFindSupplierByToken(token: string) {
  const { data, isLoading, error, get } = useApiGet<Supplier>(
    'suppliers/verify'
  );
  const findSupplier = useCallback(() => {
    get({ token });
  }, [get, token]);
  return { supplier: data, isLoading, loadingError: error, findSupplier };
}

export function useSupplierSendInvite(id: number) {
  const { data, isLoading, error, post } = useApiPost<Supplier>(
    `suppliers/${id}/send-invitation`
  );
  return {
    invitedSupplier: data,
    isInviting: isLoading,
    inviteError: error,
    inviteSupplier: post
  };
}

// spor

export function useFindPurchaseOrders() {
  const { get, ...result } = useApiGet<PaginatedList<SupplierPurchaseOrder>>(
    'spor'
  );
  const findPurchaseOrders = useIdSearchableFetch(get, WorkflowEntity.Spor);
  return { ...result, get: findPurchaseOrders };
}

export function useFindPurchaseOrderByTask(taskId: number) {
  const { data, isLoading, error, get } = useApiGet<SupplierPurchaseOrder>(
    'spor/task/' + taskId
  );
  return { data, isLoading, error, get };
}

export function useCreateSpor() {
  const { data, isLoading, error, post } = useApiPost<SupplierPurchaseOrder>(
    'spor'
  );
  const createSpor = useCallback(
    (dto: SupplierPurchaseOrderCreateDto) => post(dto),
    [post]
  );
  return { spor: data, isCreating: isLoading, createError: error, createSpor };
}

export function useUpdateSpor(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<SupplierPurchaseOrder>(
    `spor/${id}`
  );
  return {
    updatedSpor: data,
    isUpdating: isLoading,
    updateError: error,
    updateSpor: patch
  };
}

export function handleFormNumber(n: any): number | '' {
  return typeof n === 'number' ? n : '';
}

export function convertEmptyToNull<T extends Record<string, any>>(
  obj: T,
  ...keys: Array<keyof T>
): T {
  for (const key of keys) {
    if (obj[key] === '') {
      obj[key] = null as T[keyof T];
    }
  }
  return obj;
}
